import React, { useCallback, useMemo } from 'react';
import TransactionList from '../../../../../Detail/Transactions/TransactionList';
import { Transaction as DetailedTrasnsaction } from '../../../../../Overview/types';
import {
  CbrtRangeState,
  CompactTransaction,
  CurrentProfit,
  MrcState,
  ProfitResultIndicatorConfigs,
  Transaction,
  WakeupTimes,
} from '../../../../types';
import Summary from './Summary';

import './index.css';
import {
  ComplexParamsBundle,
  LatestBotState,
} from '../../../../../Detail/types';

interface Props {
  transactions: Transaction[] | undefined;
  baseAsset: string;
  quoteAsset: string;
  currentProfit: CurrentProfit | undefined;
  compactTransactionLength: number | undefined;
  allCompactTransactions: CompactTransaction[];
  currentComplexParams: ComplexParamsBundle | undefined;
  cbrtRangeStates: CbrtRangeState[];
  mrcStates: MrcState[];
  latestBotState: LatestBotState | undefined;
  wakeupTimes: WakeupTimes;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

const Transactions = (props: Props) => {
  const {
    transactions,
    baseAsset,
    quoteAsset,
    currentProfit,
    compactTransactionLength,
    allCompactTransactions,
    currentComplexParams,
    cbrtRangeStates,
    mrcStates,
    latestBotState,
    wakeupTimes,
    indicatorConfig,
  } = props;

  const formatTransactionType = useCallback(
    (transactions: Transaction[]) => {
      return transactions.map((t, i) => {
        return {
          id: Math.floor(Math.random() * 9999999999),
          index: `${i + 1}`,
          botId: 1,
          baseAsset,
          quoteAsset,
          transactionIndex: t.id,
          subscriptionId: 0,
          compactTransactionId: t.compactTransactionId
            ? t.compactTransactionId.toString()
            : null,
          orderId: null,
          orderType: t.orderType,
          transactionType: t.transactionType,
          orderPrice: t.orderPrice,
          assetQuantity: t.assetQuantity,
          stableQuantity: t.stableQuantity,
          currentAssetBalance: t.balanceInAsset,
          currentStableBalance: t.balanceInUsdt,
          totalBalance: t.balanceInUsdt + t.balanceInAsset * t.orderPrice,
          currentFee: t.currentFee,
          totalFee: t.totalFee,
          isComplete: t.isComplete ? 1 : 0,
          success: t.success,
          profit: t.transactionType.includes('TAKE_PROFIT')
            ? t.profit?.profitSincePreviousNonTpt
            : t.orderType === 'BUY'
            ? t.profitInAsset
            : t.profitInUsdt,
          currentProfit: null,
          transactionTimestamp: t.timestamp,
          transactionDate: t.date,
          initialVolume: 0,
          initialVolumeAsset: 'USD',
          currentCbr: null,
          cbrt: 0,
          shorterTimeFrameInterval: 1,
          mrcEjectedPrice: null,
          mrcAcceptedPrice: null,
          multiOrderVolume: null,
          multiOrderIndex: null,
          customBalanceUsed: null,
          customBalanceSizeUsed: null,
          extraInfo: null,
          actions: null,
          averageOrderPrice: (t as any).averageOrderPrice,
          candles: t.candles,
          complexParams: currentComplexParams?.complexParams,
          timeFrames: currentComplexParams?.timeFrames,
          signals: t.signals,
          exchange: t.exchange,
        } as DetailedTrasnsaction;
      });
    },
    [
      baseAsset,
      currentComplexParams?.complexParams,
      currentComplexParams?.timeFrames,
      quoteAsset,
    ],
  );

  const transactionDetails = useMemo(() => {
    const allTransactions = formatTransactionType(transactions || []).sort(
      (a, b) => b.transactionTimestamp - a.transactionTimestamp,
    );

    const compactTransactions = formatTransactionType(
      allCompactTransactions || [],
    ).sort((a, b) => b.transactionTimestamp - a.transactionTimestamp);

    allTransactions.forEach(
      (t) =>
        (t.index = compactTransactions.find(
          (ct) => ct.compactTransactionId === t.compactTransactionId,
        )?.index),
    );

    return {
      allTransactions,
      compactTransactions,
      currentProfit,
    };
  }, [
    allCompactTransactions,
    currentProfit,
    formatTransactionType,
    transactions,
  ]);

  if (!transactions) {
    return null;
  }

  return (
    <div className="result-transactions-root">
      <Summary compactTransactions={allCompactTransactions} />
      <TransactionList
        transactions={transactionDetails}
        isLoading={false}
        compactTransactionLength={compactTransactionLength}
        currentComplexParams={currentComplexParams}
        profitCalcsCurrentProfit={currentProfit}
        cbrtRangeStates={cbrtRangeStates}
        mrcStates={mrcStates}
        latestBotState={latestBotState}
        wakeupTimes={wakeupTimes}
        indicatorConfig={indicatorConfig}
      />
    </div>
  );
};

export default React.memo(Transactions);
